import Phaser from 'phaser'
import { InventorySystem } from '../systems/InventorySystem'
import { ITEMS, INVENTORY_EVENTS } from '../types/GameTypes'

const SLOT_SIZE = 48
const SLOTS_PER_ROW = 4
const ROWS = 5
const PADDING = 10
const SLOT_SPACING = 4

export class InventoryUI {
  private scene: Phaser.Scene
  private inventory: InventorySystem

  // Main container (the panel)
  private panel: Phaser.GameObjects.Container
  private isOpen: boolean = false

  // Panel elements
  private background!: Phaser.GameObjects.Graphics
  private titleText!: Phaser.GameObjects.Text
  private goldText!: Phaser.GameObjects.Text
  private closeButton!: Phaser.GameObjects.Container
  private slots: Phaser.GameObjects.Container[] = []

  // Panel dimensions
  private panelWidth: number
  private panelHeight: number

  constructor(scene: Phaser.Scene, inventory: InventorySystem) {
    this.scene = scene
    this.inventory = inventory

    // Calculate panel size
    this.panelWidth = PADDING * 2 + SLOTS_PER_ROW * SLOT_SIZE + (SLOTS_PER_ROW - 1) * SLOT_SPACING
    this.panelHeight = PADDING * 2 + 60 + ROWS * SLOT_SIZE + (ROWS - 1) * SLOT_SPACING + 10

    // Create panel (hidden by default)
    this.panel = scene.add.container(0, 0)
    this.panel.setDepth(2000)
    this.panel.setVisible(false)

    this.createPanel()
    this.createSlots()
    this.positionPanel()

    // Listen for inventory changes
    this.scene.events.on(INVENTORY_EVENTS.INVENTORY_CHANGED, this.updateSlots, this)

    // Listen for resize
    this.scene.scale.on('resize', this.positionPanel, this)
  }

  /**
   * Create the panel background and header
   */
  private createPanel(): void {
    // Background
    this.background = this.scene.add.graphics()
    this.background.fillStyle(0x1f2937, 0.95)
    this.background.fillRoundedRect(0, 0, this.panelWidth, this.panelHeight, 12)
    this.background.lineStyle(2, 0x4b5563)
    this.background.strokeRoundedRect(0, 0, this.panelWidth, this.panelHeight, 12)
    this.panel.add(this.background)

    // Title
    this.titleText = this.scene.add.text(PADDING, PADDING, 'Inventory', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    })
    this.panel.add(this.titleText)

    // Gold display
    const goldIcon = this.scene.add.graphics()
    goldIcon.fillStyle(0xffd700)
    goldIcon.fillCircle(PADDING + 8, PADDING + 40, 8)
    goldIcon.fillStyle(0xffed4a)
    goldIcon.fillCircle(PADDING + 6, PADDING + 38, 4)
    this.panel.add(goldIcon)

    this.goldText = this.scene.add.text(PADDING + 22, PADDING + 32, '0', {
      fontSize: '16px',
      color: '#fbbf24',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    })
    this.panel.add(this.goldText)

    // Close button
    this.closeButton = this.scene.add.container(this.panelWidth - PADDING - 12, PADDING + 12)

    const closeCircle = this.scene.add.circle(0, 0, 12, 0xef4444)
    closeCircle.setInteractive({ useHandCursor: true })

    const closeX = this.scene.add.text(0, 0, 'X', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    this.closeButton.add([closeCircle, closeX])
    this.panel.add(this.closeButton)

    // Close button events
    closeCircle.on('pointerdown', () => this.close())
    closeCircle.on('pointerover', () => closeCircle.setFillStyle(0xdc2626))
    closeCircle.on('pointerout', () => closeCircle.setFillStyle(0xef4444))

    // Make background block clicks
    const blocker = this.scene.add.rectangle(
      this.panelWidth / 2,
      this.panelHeight / 2,
      this.panelWidth,
      this.panelHeight,
      0x000000,
      0
    )
    blocker.setInteractive()
    this.panel.add(blocker)
    this.panel.sendToBack(blocker)
  }

  /**
   * Create inventory slots
   */
  private createSlots(): void {
    const startX = PADDING
    const startY = PADDING + 60

    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < SLOTS_PER_ROW; col++) {
        const x = startX + col * (SLOT_SIZE + SLOT_SPACING)
        const y = startY + row * (SLOT_SIZE + SLOT_SPACING)
        const slotIndex = row * SLOTS_PER_ROW + col

        const slot = this.createSlot(x, y, slotIndex)
        this.slots.push(slot)
        this.panel.add(slot)
      }
    }
  }

  /**
   * Create a single inventory slot
   */
  private createSlot(x: number, y: number, index: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y)

    // Slot background
    const bg = this.scene.add.graphics()
    bg.fillStyle(0x374151, 1)
    bg.fillRoundedRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6)
    bg.lineStyle(1, 0x4b5563)
    bg.strokeRoundedRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6)
    container.add(bg)

    // Item icon (graphics, will be drawn when item is present)
    const icon = this.scene.add.graphics()
    icon.setPosition(SLOT_SIZE / 2, SLOT_SIZE / 2)
    container.add(icon)

    // Quantity text
    const qtyText = this.scene.add.text(SLOT_SIZE - 4, SLOT_SIZE - 4, '', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(1, 1)
    container.add(qtyText)

    // Make interactive
    const hitArea = this.scene.add.rectangle(
      SLOT_SIZE / 2,
      SLOT_SIZE / 2,
      SLOT_SIZE,
      SLOT_SIZE,
      0x000000,
      0
    )
    hitArea.setInteractive({ useHandCursor: true })
    container.add(hitArea)

    // Click to use item
    hitArea.on('pointerdown', () => this.onSlotClick(index))
    hitArea.on('pointerover', () => {
      bg.clear()
      bg.fillStyle(0x4b5563, 1)
      bg.fillRoundedRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6)
      bg.lineStyle(2, 0x60a5fa)
      bg.strokeRoundedRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6)
    })
    hitArea.on('pointerout', () => {
      bg.clear()
      bg.fillStyle(0x374151, 1)
      bg.fillRoundedRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6)
      bg.lineStyle(1, 0x4b5563)
      bg.strokeRoundedRect(0, 0, SLOT_SIZE, SLOT_SIZE, 6)
    })

    // Store references for updating
    container.setData('icon', icon)
    container.setData('qtyText', qtyText)
    container.setData('index', index)

    return container
  }

  /**
   * Handle slot click
   */
  private onSlotClick(index: number): void {
    const item = this.inventory.getSlot(index)
    if (!item) return

    const itemDef = ITEMS[item.itemId]
    if (!itemDef) return

    // Use potions on click
    if (itemDef.type === 'potion') {
      const used = this.inventory.useItem(item.itemId)
      if (used) {
        // Visual feedback
        const slot = this.slots[index]
        this.scene.tweens.add({
          targets: slot,
          scale: 0.9,
          duration: 50,
          yoyo: true
        })
      }
    }
  }

  /**
   * Update all slots to reflect current inventory
   */
  private updateSlots(): void {
    const inventorySlots = this.inventory.getSlots()

    // Update gold
    this.goldText.setText(this.inventory.getGold().toString())

    // Update each slot
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]
      const icon = slot.getData('icon') as Phaser.GameObjects.Graphics
      const qtyText = slot.getData('qtyText') as Phaser.GameObjects.Text
      const item = inventorySlots[i]

      icon.clear()

      if (item) {
        this.drawItemIcon(icon, item.itemId)
        qtyText.setText(item.quantity > 1 ? item.quantity.toString() : '')
      } else {
        qtyText.setText('')
      }
    }
  }

  /**
   * Draw an item icon
   */
  private drawItemIcon(graphics: Phaser.GameObjects.Graphics, itemId: string): void {
    const itemDef = ITEMS[itemId]
    if (!itemDef) return

    switch (itemId) {
      case 'healthPotion':
        // Red potion bottle
        graphics.fillStyle(0xef4444)
        graphics.fillRoundedRect(-10, -8, 20, 20, 4)
        graphics.fillStyle(0xfca5a5)
        graphics.fillRect(-7, -5, 6, 10)
        // Bottle neck
        graphics.fillStyle(0x9ca3af)
        graphics.fillRect(-4, -14, 8, 6)
        // Cork
        graphics.fillStyle(0xa16207)
        graphics.fillRect(-3, -16, 6, 4)
        break

      case 'smallHealthPotion':
        // Smaller red potion
        graphics.fillStyle(0xef4444)
        graphics.fillRoundedRect(-8, -6, 16, 16, 3)
        graphics.fillStyle(0xfca5a5)
        graphics.fillRect(-5, -3, 5, 8)
        // Bottle neck
        graphics.fillStyle(0x9ca3af)
        graphics.fillRect(-3, -11, 6, 5)
        // Cork
        graphics.fillStyle(0xa16207)
        graphics.fillRect(-2, -13, 4, 3)
        break

      case 'manaPotion':
        // Blue potion bottle
        graphics.fillStyle(0x3b82f6)
        graphics.fillRoundedRect(-10, -8, 20, 20, 4)
        graphics.fillStyle(0x93c5fd)
        graphics.fillRect(-7, -5, 6, 10)
        // Bottle neck
        graphics.fillStyle(0x9ca3af)
        graphics.fillRect(-4, -14, 8, 6)
        // Cork
        graphics.fillStyle(0xa16207)
        graphics.fillRect(-3, -16, 6, 4)
        break

      case 'gold':
        // Gold coins
        graphics.fillStyle(0xffd700)
        graphics.fillCircle(-4, 2, 8)
        graphics.fillCircle(4, -2, 8)
        graphics.fillStyle(0xffed4a)
        graphics.fillCircle(-5, 0, 4)
        graphics.fillCircle(3, -4, 4)
        break
    }
  }

  /**
   * Position panel in center of screen
   */
  private positionPanel(): void {
    const width = this.scene.scale.width
    const height = this.scene.scale.height

    this.panel.setPosition(
      (width - this.panelWidth) / 2,
      (height - this.panelHeight) / 2
    )
  }

  /**
   * Open the inventory panel
   */
  public open(): void {
    if (this.isOpen) return

    this.isOpen = true
    this.updateSlots()
    this.panel.setVisible(true)
    this.panel.setAlpha(0)
    this.panel.setScale(0.9)

    this.scene.tweens.add({
      targets: this.panel,
      alpha: 1,
      scale: 1,
      duration: 150,
      ease: 'Power2'
    })
  }

  /**
   * Close the inventory panel
   */
  public close(): void {
    if (!this.isOpen) return

    this.scene.tweens.add({
      targets: this.panel,
      alpha: 0,
      scale: 0.9,
      duration: 100,
      ease: 'Power2',
      onComplete: () => {
        this.panel.setVisible(false)
        this.isOpen = false
      }
    })
  }

  /**
   * Toggle the inventory panel
   */
  public toggle(): void {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Check if inventory is open
   */
  public getIsOpen(): boolean {
    return this.isOpen
  }

  /**
   * Update (called each frame when open)
   */
  public update(): void {
    // Nothing to update per frame currently
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off(INVENTORY_EVENTS.INVENTORY_CHANGED, this.updateSlots, this)
    this.scene.scale.off('resize', this.positionPanel, this)
    this.panel.destroy()
  }
}
